import React, { useState, useEffect, useContext } from "react";
import { doc, getDoc } from "firebase/firestore";
import { FaSearch, FaCheckCircle, FaTimesCircle, FaBug } from "react-icons/fa";
import { toast } from "react-toastify";
import { db } from "../../firebase";
import { AuthContext } from "../../AuthProvider";

const sanitiseEmail = (email) => {
  const sanitized = email.trim().toLowerCase();
  return sanitized;
};

const BlacklistChecker = ({
  teamId,
  selectedTeam,
  teams,
  onTeamChange,
  debugEnabled = false,
  onDebugToggle,
}) => {
  const { user } = useContext(AuthContext);

  // State for the actual checker functionality
  const [emailsText, setEmails] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  // Conditional logging helper
  const debugLog = (...args) => {
    if (debugEnabled) {
      console.log("[BlacklistChecker]", ...args);
    }
  };

  const debugError = (...args) => {
    if (debugEnabled) {
      console.error("[BlacklistChecker]", ...args);
    }
  };

  const debugLogWithEmail = (email, sanitized) => {
    if (debugEnabled) {
      console.log(`DEBUG - Sanitizing: "${email}" → "${sanitized}"`);
    }
  };

  // Check emails against blacklist
  const checkEmails = async () => {
    if (!user) {
      return toast.error("You must be logged in to use this feature");
    }

    if (!teamId) {
      return toast.error("No team selected");
    }

    let list = emailsText
      .split(/[\s,]+/)
      .map((e) => e.trim())
      .filter(Boolean);

    if (list.length === 0) {
      return toast.warn("Enter at least one e-mail");
    }
    if (list.length > 50) {
      return toast.error("Max 50 emails at once");
    }

    debugLog("=== BLACKLIST CHECK START ===");
    debugLog("Team ID:", teamId);
    debugLog("Selected team:", selectedTeam);
    debugLog("User:", { uid: user.uid, email: user.email });

    setLoading(true);
    try {
      const out = [];

      // Sequential fetch for blacklist docs
      for (const raw of list) {
        const id = sanitiseEmail(raw);
        const ref = doc(db, "teams", teamId, "blacklist", id);

        debugLogWithEmail(raw, id);
        debugLog(`Document path: "teams/${teamId}/blacklist/${id}"`);

        try {
          const snap = await getDoc(ref);
          debugLog(`Document exists: ${snap.exists()}`);
          out.push({
            email: raw,
            found: snap.exists(),
          });
        } catch (err) {
          debugError(`Error checking "${id}":`, err);
          out.push({
            email: raw,
            error: err.message,
          });
        }
      }

      setResults(out);
      debugLog("Check complete:", out);
    } catch (err) {
      debugError("Global error:", err);
      toast.error("Lookup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <h1 className="text-2xl font-bold">Blacklist Checker</h1>
          {selectedTeam && (
            <span className="ml-3 text-sm text-gray-600">
              Team: <span className="font-medium">{selectedTeam.name}</span>
            </span>
          )}
        </div>

        <div className="flex items-center space-x-4">
          {/* Team selector for multiple teams */}
          {teams && teams.length > 1 && (
            <button
              onClick={onTeamChange}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Change Team
            </button>
          )}

          {/* Debug Logging Toggle */}
          {onDebugToggle && (
            <div className="flex items-center space-x-2">
              <FaBug
                className={`${
                  debugEnabled ? "text-green-600" : "text-gray-400"
                }`}
              />
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={debugEnabled}
                  onChange={(e) => onDebugToggle(e.target.checked)}
                  className="form-checkbox h-4 w-4 text-purple-600"
                />
                <span className="text-sm text-gray-700">Debug Logging</span>
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Email input textarea */}
      <textarea
        value={emailsText}
        onChange={(e) => setEmails(e.target.value)}
        placeholder="Paste up to 50 email addresses – one per line, comma or space-separated"
        rows={6}
        className="w-full p-3 border rounded focus:ring-2 focus:ring-purple-500 mb-4 resize-y"
      />

      <button
        onClick={checkEmails}
        disabled={loading || !teamId}
        className="bg-purple-600 text-white px-6 py-2 rounded flex items-center hover:bg-purple-700 disabled:opacity-50"
      >
        <FaSearch className="mr-2" />
        {loading ? "Checking…" : "Check Emails"}
      </button>

      {/* Results */}
      {results.length > 0 && (
        <div className="mt-8 bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {results.map(({ email, found, error }) => (
                <tr
                  key={email}
                  className={
                    error ? "bg-yellow-50" : found ? "bg-red-50" : "bg-green-50"
                  }
                >
                  <td className="px-4 py-3 font-mono break-all">{email}</td>
                  <td className="px-4 py-3 flex items-center space-x-2">
                    {error ? (
                      <>
                        <FaTimesCircle className="text-yellow-600" />
                        <span className="text-yellow-600 font-semibold">
                          Error: {error}
                        </span>
                      </>
                    ) : found ? (
                      <>
                        <FaTimesCircle className="text-red-600" />
                        <span className="text-red-600 font-semibold">
                          Black-listed – do NOT use
                        </span>
                      </>
                    ) : (
                      <>
                        <FaCheckCircle className="text-green-600" />
                        <span className="text-green-600 font-semibold">
                          Safe to use
                        </span>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BlacklistChecker;
